/**
 * ContributeProgress — standalone contributor page for team progress notifications.
 *
 * Token-gated, no Auth0 required. Accessible to notification invitees via a link like:
 *   /roadmap/build_roadmap/contribute/:roundId/:token
 *
 * Shows: who invited you, period, deadline, WMBT items + block context, save form.
 */

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, ConfigProvider, Input, Slider, Spin } from "antd";
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Lock,
  Save,
} from "lucide-react";
import { VojomoSymbol } from "./components/VojomoSymbol";
import "./rb-globals.css";

const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

// ── Types ──────────────────────────────────────────────────────────────────────

interface ContributionContext {
  roundId: string;
  token: string;
  participant: { name: string; email: string; userId: string | null };
  session: { companyName: string };
  round: {
    periodType: string;
    periodKey: string;
    periodLabel: string;
    kickedOffByName: string;
    deadline: string | null;
    personalMessage: string | null;
    status: string;
  };
  block: {
    wmbt: { label: string; value: string | null; categoryObjective?: string | null }[];
    draft: string;
  };
  existingContribution: {
    state: Record<string, { pct_complete?: number | null; rationale?: string | null; whats_working?: string | null; challenges?: string | null; focus_ahead?: string | null; last_saved_at?: string }>;
    lockedAt: string | null;
  } | null;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDeadline(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// Score scale is always 0–100%. isQbr param is kept for call-site compatibility
// but the /10 normalization is dropped — scores are now stored 0–100 for both types.
function scoreColor(score: number | null, _isQbr: boolean): string {
  if (score === null) return "#94a3b8";
  if (score >= 80) return "#22c55e";
  if (score >= 50) return "#f59e0b";
  return "#ef4444";
}

// ── WmbtContributeRow ──────────────────────────────────────────────────────────

interface WmbtContributeRowProps {
  index: number;
  item: { label: string; value: string | null; categoryObjective?: string | null };
  state: { pct_complete?: number | null; rationale?: string | null; whats_working?: string | null; challenges?: string | null; focus_ahead?: string | null; last_saved_at?: string } | undefined;
  onChange: (index: number, field: string, value: string | number | null) => void;
  isQbr: boolean;
  locked: boolean;
}

function WmbtContributeRow({ index, item, state, onChange, isQbr, locked }: WmbtContributeRowProps) {
  const [expanded, setExpanded] = useState(true);
  const label = item.categoryObjective || item.label || `Objective ${index + 1}`;
  const score = state?.pct_complete ?? null;
  // Score scale is always 0–100% regardless of period type.
  const SCORE_MAX = 100;
  const SCORE_STEP = 5;
  const formatScore = (x: number) => `${x}%`;

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden mb-3 bg-white shadow-sm">
      <button
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 text-left transition-colors"
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span
            className="inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold text-white flex-shrink-0"
            style={{ backgroundColor: scoreColor(score, isQbr) }}
          >
            {score !== null ? `${score}` : "?"}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate">{label}</p>
            {item.value && (
              <p className="text-xs text-slate-500 truncate mt-0.5">{item.value}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-slate-500 font-medium">
            {score !== null ? formatScore(score) : "Not set"}
          </span>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 bg-slate-50 border-t border-slate-100">
          {/* Score */}
          <div className="mt-3">
            <label className="text-xs font-semibold text-slate-600 block mb-2">
              % Complete
            </label>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <ConfigProvider theme={{ token: { colorPrimary: "#2c1561" } }}>
                  <Slider
                    min={0}
                    max={SCORE_MAX}
                    step={SCORE_STEP}
                    value={score ?? 0}
                    onChange={(v) => onChange(index, "pct_complete", v)}
                    disabled={locked}
                    tooltip={{ formatter: (v) => `${v}%` }}
                  />
                </ConfigProvider>
              </div>
              <span
                className="text-sm font-bold w-12 text-right"
                style={{ color: scoreColor(score, isQbr) }}
              >
                {score !== null ? formatScore(score) : "–"}
              </span>
            </div>
          </div>

          {/* Qualitative fields */}
          {[
            { key: "rationale", label: "Rationale", placeholder: "Why is progress at this level?" },
            { key: "whats_working", label: "What's Working", placeholder: "Accelerators and wins…" },
            { key: "challenges", label: "Challenges", placeholder: "Obstacles and blockers…" },
            { key: "focus_ahead", label: "Focus Ahead", placeholder: "Key actions for next period…" },
          ].map(({ key, label: fieldLabel, placeholder }) => (
            <div key={key} className="mt-3">
              <label className="text-xs font-semibold text-slate-600 block mb-1">{fieldLabel}</label>
              <Input.TextArea
                value={((state as unknown) as Record<string, string | null | number> | undefined)?.[key] as string ?? ""}
                onChange={(e) => onChange(index, key, e.target.value)}
                placeholder={placeholder}
                rows={2}
                disabled={locked}
                className="text-sm"
              />
            </div>
          ))}

          {state?.last_saved_at && (
            <p className="text-xs text-slate-400 mt-2">
              Last saved {new Date(state.last_saved_at).toLocaleString()}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export function ContributeProgress() {
  const { roundId, token } = useParams<{ roundId: string; token: string }>();
  const [context, setContext] = useState<ContributionContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [localState, setLocalState] = useState<Record<string, { pct_complete?: number | null; rationale?: string | null; whats_working?: string | null; challenges?: string | null; focus_ahead?: string | null }>>({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    if (!roundId || !token) return;
    setLoading(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/rb/contribute/${roundId}/${token}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Invalid or expired link");
      }
      const data: ContributionContext = await res.json();
      setContext(data);
      // Pre-populate from existing contribution
      if (data.existingContribution?.state) {
        const init: typeof localState = {};
        for (const [key, val] of Object.entries(data.existingContribution.state)) {
          init[key] = {
            pct_complete: val.pct_complete ?? null,
            rationale: val.rationale ?? null,
            whats_working: val.whats_working ?? null,
            challenges: val.challenges ?? null,
            focus_ahead: val.focus_ahead ?? null,
          };
        }
        setLocalState(init);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load contribution context");
    } finally {
      setLoading(false);
    }
  }, [roundId, token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFieldChange = (index: number, field: string, value: string | number | null) => {
    setLocalState((prev) => ({
      ...prev,
      [String(index)]: {
        pct_complete: prev[String(index)]?.pct_complete ?? null,
        rationale: prev[String(index)]?.rationale ?? null,
        whats_working: prev[String(index)]?.whats_working ?? null,
        challenges: prev[String(index)]?.challenges ?? null,
        focus_ahead: prev[String(index)]?.focus_ahead ?? null,
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    if (!roundId || !token) return;
    setSaving(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/rb/contribute/${roundId}/${token}/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ state: localState }),
      });
      if (res.ok) {
        setSavedAt(new Date());
      }
    } catch (e) {
      console.error("Failed to save", e);
    } finally {
      setSaving(false);
    }
  };

  // ── Loading ──

  if (loading) {
    return (
      <div className="rb-root min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <Spin size="large" />
          <p className="text-sm text-slate-500">Loading your contribution form…</p>
        </div>
      </div>
    );
  }

  if (error || !context) {
    return (
      <div className="rb-root min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-sm px-6">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-800 mb-2">Link not valid</h2>
          <p className="text-sm text-slate-500">{error || "This link may have expired or is invalid."}</p>
        </div>
      </div>
    );
  }

  const { round, session, block } = context;
  const isQbr = round.periodType === "qbr";
  const isLocked =
    Boolean(context.existingContribution?.lockedAt) || round.status === "closed";

  return (
    <div className="rb-root min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <VojomoSymbol style={{ width: 32, height: 32 }} />
          <div>
            <h1 className="text-lg font-bold text-slate-800">{session.companyName}</h1>
            <p className="text-sm text-slate-500">Progress Contribution</p>
          </div>
        </div>

        {/* Context card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800">
                {round.periodLabel}
                {isQbr ? " Quarterly" : " Weekly Progress"}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                Invited by <span className="font-medium">{round.kickedOffByName}</span>
              </p>
            </div>
            <span
              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium"
              style={{ backgroundColor: "#ede9fe", color: "#2c1561" }}
            >
              {isQbr ? "Quarterly" : "Weekly"}
            </span>
          </div>

          {round.deadline && (
            <div className="flex items-center gap-1.5 mt-3 text-xs text-amber-700 bg-amber-50 px-2.5 py-1.5 rounded-lg">
              <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
              <span>
                Deadline: <strong>{formatDeadline(round.deadline)}</strong>
              </span>
            </div>
          )}

          {round.personalMessage && (
            <p className="text-sm text-slate-600 mt-3 border-t border-slate-100 pt-3 italic">
              "{round.personalMessage}"
            </p>
          )}
        </div>

        {/* Block context (draft narrative) */}
        {block.draft && (
          <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 mb-6">
            <p className="text-xs font-semibold text-indigo-700 mb-1.5">
              {isQbr ? "1-Year Objectives Context" : "Quarterly Objectives Context"}
            </p>
            <p className="text-sm text-slate-700 leading-relaxed line-clamp-4">{block.draft}</p>
          </div>
        )}

        {/* Locked banner */}
        {isLocked && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4 text-sm text-amber-700">
            <Lock className="w-4 h-4 flex-shrink-0" />
            <span>
              {round.status === "closed"
                ? "This period is closed. No further changes can be made."
                : "Your contribution has been locked."}
            </span>
          </div>
        )}

        {/* Objectives */}
        {block.wmbt.length === 0 ? (
          <div className="text-center py-12 text-slate-400 text-sm">
            No objectives have been defined for this session yet.
          </div>
        ) : (
          <>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
              {isQbr ? "1-Year Objectives" : "Quarterly Objectives"} — {block.wmbt.length} item
              {block.wmbt.length !== 1 ? "s" : ""}
            </p>

            {block.wmbt.map((item, index) => (
              <WmbtContributeRow
                key={index}
                index={index}
                item={item}
                state={localState[String(index)]}
                onChange={handleFieldChange}
                isQbr={isQbr}
                locked={isLocked}
              />
            ))}

            {/* Save footer */}
            {!isLocked && (
              <div className="mt-6 flex items-center justify-between bg-white rounded-xl border border-slate-200 px-4 py-3 shadow-sm">
                <div className="text-xs text-slate-400">
                  {savedAt ? (
                    <span className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                      Saved at {savedAt.toLocaleTimeString()}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      Save anytime — you can update until the deadline
                    </span>
                  )}
                </div>
                <ConfigProvider theme={{ token: { colorPrimary: "#2c1561" } }}>
                  <Button
                    type="primary"
                    loading={saving}
                    icon={<Save className="w-3.5 h-3.5" />}
                    onClick={handleSave}
                    disabled={Object.keys(localState).length === 0}
                  >
                    Save my progress
                  </Button>
                </ConfigProvider>
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-slate-400">
            Powered by <span className="font-semibold text-[#2c1561]">Vojomo</span>
          </p>
        </div>
      </div>
    </div>
  );
}
